import { FC, type ComponentProps } from 'react';
import { Button, Calendar, Card, Flex, Tag, Typography } from 'antd';
import { TbCalendarEvent } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { dayjs } from '@/utilities';

import CalendarEventKeyframe from './CalendarEventKeyframe';
import { CALENDAR_EVENT_SOURCE_IDS, type MarketCalendarEvent } from './calendarEvents';
import type { CalendarDate, CalendarViewMode } from './calendarPeriod';

type AntCalendarProps = ComponentProps<typeof Calendar>;

interface CalendarPeriodViewProps {
    eventsByDate: Readonly<Record<string, readonly MarketCalendarEvent[]>>;
    onSelectDate: (date: CalendarDate) => void;
    value: CalendarDate;
    view: CalendarViewMode;
}

const calendarEventLabel = (event: MarketCalendarEvent): string =>
    event.source === CALENDAR_EVENT_SOURCE_IDS.dividend ? event.symbol : event.title;

const EventRows: FC<{
    events: readonly MarketCalendarEvent[];
    onSelect: () => void;
}> = ({ events, onSelect }) => {
    const { token } = useAntDesignTheme();

    return (
        <Flex gap={4} vertical>
            {events.map((event) => (
                <Button
                    key={event.id}
                    block
                    onClick={onSelect}
                    style={{ height: 'auto', textAlign: 'start' }}
                    type='text'
                >
                    <Flex align='center' gap={6} style={{ minWidth: 0 }}>
                        <CalendarEventKeyframe size='0.75rem' source={event.source} />
                        <Typography.Text ellipsis style={{ flex: '1 1 0%', minWidth: 0 }}>
                            {calendarEventLabel(event)}
                        </Typography.Text>
                        {event.source === CALENDAR_EVENT_SOURCE_IDS.economic && event.impact !== 'unknown' ? (
                            <Tag bordered={false} style={{ marginInlineEnd: 0 }}>
                                {event.impact}
                            </Tag>
                        ) : null}
                        {event.startsAt.length > 10 ? (
                            <Typography.Text style={{ color: token.colorTextTertiary, flex: 'none' }}>
                                {dayjs(event.startsAt).format('LT')}
                            </Typography.Text>
                        ) : null}
                    </Flex>
                </Button>
            ))}
        </Flex>
    );
};

const DayView: FC<Omit<CalendarPeriodViewProps, 'view'>> = ({ eventsByDate, onSelectDate, value }) => {
    const dateEvents = eventsByDate[value.format('YYYY-MM-DD')] ?? [];

    if (!dateEvents.length) {
        return (
            <EmptySection
                description='Dividend and economic events scheduled for this day will appear here.'
                height='20rem'
                icon={<TbCalendarEvent />}
                label='No Events Scheduled'
            />
        );
    }

    return (
        <Card
            size='small'
            title={value.format('dddd, MMMM D')}
            styles={{ body: { maxHeight: '24rem', overflow: 'hidden', padding: 0 } }}
        >
            <OverflowFadeScrollArea
                aria-label='Calendar day events'
                containerStyle={{ maxHeight: '24rem' }}
                style={{ padding: '0.5rem' }}
                surface='container'
            >
                <EventRows events={dateEvents} onSelect={() => onSelectDate(value)} />
            </OverflowFadeScrollArea>
        </Card>
    );
};

const WeekView: FC<Omit<CalendarPeriodViewProps, 'view'>> = ({ eventsByDate, onSelectDate, value }) => {
    const { token } = useAntDesignTheme();
    const start = value.startOf('week');
    const dates = Array.from({ length: 7 }, (_, index) => start.add(index, 'day'));

    return (
        <div style={{ minWidth: '56rem', overflow: 'hidden', width: '100%' }}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    overflow: 'hidden',
                }}
            >
                {dates.map((date, index) => {
                    const dateEvents = eventsByDate[date.format('YYYY-MM-DD')] ?? [];
                    const isToday = date.isSame(dayjs(), 'day');

                    return (
                        <section
                            key={date.format('YYYY-MM-DD')}
                            style={{
                                borderInlineEnd:
                                    index < dates.length - 1 ? `1px solid ${token.colorBorder}` : undefined,
                                minHeight: '22rem',
                                minWidth: 0,
                                padding: '0.5rem',
                            }}
                        >
                            <Button
                                block
                                onClick={() => onSelectDate(date)}
                                style={{ height: 'auto', marginBottom: '0.5rem' }}
                                type='text'
                            >
                                <Flex align='baseline' gap={5} justify='space-between'>
                                    <Typography.Text type='secondary'>{date.format('ddd')}</Typography.Text>
                                    <Typography.Text style={{ color: isToday ? token.colorPrimary : undefined }} strong>
                                        {date.format('D')}
                                    </Typography.Text>
                                </Flex>
                            </Button>
                            {dateEvents.length ? (
                                <EventRows events={dateEvents} onSelect={() => onSelectDate(date)} />
                            ) : (
                                <Typography.Text style={{ color: token.colorTextQuaternary }}>
                                    No events
                                </Typography.Text>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
};

const QuarterView: FC<Omit<CalendarPeriodViewProps, 'view'>> = ({ eventsByDate, onSelectDate, value }) => {
    const quarterStart = value.startOf('quarter');
    const months = Array.from({ length: 3 }, (_, index) => quarterStart.add(index, 'month'));
    const cellRender: NonNullable<AntCalendarProps['cellRender']> = (date, info) => {
        if (info.type !== 'date') {
            return info.originNode;
        }

        const count = eventsByDate[date.format('YYYY-MM-DD')]?.length ?? 0;
        return count ? (
            <Typography.Text style={{ fontSize: '0.625rem' }} type='secondary'>
                {count.toLocaleString()}
            </Typography.Text>
        ) : null;
    };

    return (
        <div
            style={{
                display: 'grid',
                gap: '0.75rem',
                gridTemplateColumns: 'repeat(3, minmax(16rem, 1fr))',
                minWidth: '50rem',
                width: '100%',
            }}
        >
            {months.map((month) => (
                <Card key={month.format('YYYY-MM')} size='small' title={month.format('MMMM')}>
                    <Calendar
                        cellRender={cellRender}
                        fullscreen={false}
                        headerRender={() => null}
                        onSelect={(date, selectInfo) => {
                            if (selectInfo.source === 'date') {
                                onSelectDate(dayjs(date.format('YYYY-MM-DD')));
                            }
                        }}
                        value={month.startOf('month')}
                    />
                </Card>
            ))}
        </div>
    );
};

const CalendarPeriodView: FC<CalendarPeriodViewProps> = (props) => {
    if (props.view === 'day') {
        return <DayView {...props} />;
    }
    if (props.view === 'week') {
        return <WeekView {...props} />;
    }

    return <QuarterView {...props} />;
};

export default CalendarPeriodView;
